import { type FC, type ReactNode } from "react";

import { clsx } from "clsx";

import { faChevronLeft } from "@fortawesome/pro-duotone-svg-icons/faChevronLeft";
import { faChevronRight } from "@fortawesome/pro-duotone-svg-icons/faChevronRight";
import { faEllipsis } from "@fortawesome/pro-duotone-svg-icons/faEllipsis";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

const BASE_STYLE =
  "flex h-9 items-center px-4 text-[13px] font-medium focus:ring-outline";
const SELECTED_STYLE = "surface-selected text-ink font-semibold";
const DEFAULT_STYLE = "text-muted";

const PaginationEllipsis: FC = () => (
  <div className={clsx(BASE_STYLE, DEFAULT_STYLE)}>
    <FontAwesomeIcon icon={faEllipsis} />
  </div>
);

const PaginationButton: FC<{
  disabled?: boolean;
  selected?: boolean;
  onClick(): void;
  children: ReactNode;
}> = ({ selected, disabled, onClick, children }) => (
  <button
    type="button"
    disabled={disabled}
    onClick={onClick}
    className={clsx(
      BASE_STYLE,
      selected ? SELECTED_STYLE : DEFAULT_STYLE,
      !disabled && !selected && "hover:bg-quiet-hover",
    )}
  >
    {children}
  </button>
);

const PaginationPrev: FC<{
  disabled?: boolean;
  onClick(): void;
}> = ({ disabled, onClick }) => (
  <PaginationButton aria-label="Prev" disabled={disabled} onClick={onClick}>
    <FontAwesomeIcon icon={faChevronLeft} />
  </PaginationButton>
);

const PaginationNext: FC<{
  disabled?: boolean;
  onClick(): void;
}> = ({ disabled, onClick }) => (
  <PaginationButton aria-label="Next" disabled={disabled} onClick={onClick}>
    <FontAwesomeIcon icon={faChevronRight} />
  </PaginationButton>
);

const PagionationPage: FC<{
  selected?: boolean;
  onClick(): void;
  page: number;
}> = ({ selected, onClick, page }) => (
  <PaginationButton selected={selected} disabled={selected} onClick={onClick}>
    {page}
  </PaginationButton>
);

export const PaginationStatus: FC<{
  current: number;
  pages: number;
}> = ({ current, pages }) => (
  <div className="text-muted text-[13px]">
    Page <strong>{current}</strong> of <strong>{pages}</strong>
  </div>
);

const L_WINDOW = 2;
const R_WINDOW = 2;

export const PaginationControl: FC<{
  current: number;
  pages: number;
  onPage(page: number): void;
}> = ({ current, pages, onPage }) => {
  const from = current - L_WINDOW < 1 ? 1 : current - L_WINDOW;
  const till = current + R_WINDOW > pages ? pages : current + R_WINDOW;

  const prev = current > 1;
  const next = current < pages;

  const indexes = [];
  for (let i = from; i <= till; i++) {
    indexes.push(i);
  }

  return (
    <nav
      role="navigation"
      aria-label="Pagination"
      className="pill-group overflow-hidden"
    >
      <PaginationPrev disabled={!prev} onClick={() => onPage(current - 1)} />

      {from > 1 && <PaginationEllipsis />}

      {indexes.map((page) => (
        <PagionationPage
          key={page}
          page={page}
          selected={page === current}
          onClick={() => onPage(page)}
        />
      ))}

      {till < pages && <PaginationEllipsis />}

      <PaginationNext disabled={!next} onClick={() => onPage(current + 1)} />
    </nav>
  );
};

export const Pagination: FC<{
  current: number;
  pages: number;
  count: number;
  onPage(_: number): void;
}> = (props) => {
  // One page needs no pager.
  if (props.pages <= 1) return;

  return (
    <div className="mt-4 flex items-center justify-between">
      <PaginationStatus {...props} />
      <PaginationControl {...props} />
    </div>
  );
};
